import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle, Mail } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn, validateCaprichoDeliveryZone } from '../../lib/utils';
import { useTableOrder } from '../../lib/table-context';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, formatDeliveryDate, toDateString, getOrderingWindowMessage } from '../../lib/preorder';
import { useCustomerLookup } from '../../lib/loyalty-hooks';
import { LoyaltyCard } from './LoyaltyCard';
import { useLanguage } from '../../lib/i18n';
import { getCustomerByPhone } from '../../lib/customer';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
  initialCustomer?: {
    customer_name: string;
    customer_phone: string;
    delivery_address: string;
    maps_url: string;
  } | null;
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
  preorder_date?: string;
  preorder_time_slot?: string;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading,
  initialCustomer
}: CheckoutFormProps) {
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    delivery_type: isTableOrder ? 'dine_in' : 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: isTableOrder ? tableOrder?.tableId || '' : '',
    notes: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData, string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  // Check if returning customer (already accepted terms) on phone change
  useEffect(() => {
    const checkReturningCustomer = async () => {
      const phone = formData.customer_phone.replace(/\D/g, '');
      if (phone.length < 8) {
        setIsReturningCustomer(false);
        return;
      }
      try {
        const cust = await getCustomerByPhone(phone);
        if (cust && cust.terms_accepted_at) {
          setIsReturningCustomer(true);
          setTermsAccepted(true);
          // Autofill all blank fields
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
        } else if (cust) {
          // Customer exists but hasn't accepted terms yet
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
        }
      } catch (err) {
        console.error('[CHECKOUT] checkReturningCustomer error:', err);
      }
    };
    const debounce = setTimeout(checkReturningCustomer, 500);
    return () => clearTimeout(debounce);
  }, [formData.customer_phone]);
  // Apply initialCustomer data from localStorage lookup
  useEffect(() => {
    if (initialCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_name: initialCustomer.customer_name || prev.customer_name,
        customer_phone: initialCustomer.customer_phone || prev.customer_phone,
        delivery_address: initialCustomer.delivery_address || prev.delivery_address,
        maps_url: initialCustomer.maps_url || prev.maps_url
      }));
    }
  }, [initialCustomer]);
  const isPreorder = isPreorderRestaurant(restaurant?.id);
  const preorderSaturdays = isPreorder ? getUpcomingSaturdays(3) : [];
  const preorderWindow = isPreorder ? getOrderingWindowMessage() : null;
  // Loyalty Integration
  const {
    customer,
    loading: customerLoading
  } = useCustomerLookup(formData.customer_phone, restaurant?.id);
  // Autofill when customer is found
  useEffect(() => {
    if (customer && !formData.customer_name) {
      setFormData(prev => ({
        ...prev,
        customer_name: customer.full_name || prev.customer_name
      }));
    }
  }, [customer]);
  // Autofill address when found
  useEffect(() => {
    if (customer?.address_text && !formData.delivery_address && formData.delivery_type === 'delivery') {
      setFormData(prev => ({
        ...prev,
        delivery_address: customer.address_text || prev.delivery_address,
        maps_url: customer.maps_url || prev.maps_url
      }));
    }
  }, [customer, formData.delivery_type]);
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const handleDeliveryTypeChange = (type: DeliveryType) => {
    setFormData(prev => ({
      ...prev,
      delivery_type: type
    }));
  };
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData, string>> = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'El nombre es requerido';
    // Phone is optional for QR table orders, required otherwise
    if (!isTableOrder) {
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
        newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
      }
    } else if (formData.customer_phone.trim() && formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
      // GPS is mandatory for delivery
      if (formData.delivery_lat === null || formData.delivery_lng === null) {
        ;
        (newErrors as any).maps_url = 'La ubicación GPS es obligatoria para delivery';
      }
      // Capricho de Canela: validate delivery zone (5km radius)
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho && formData.delivery_lat !== null && formData.delivery_lng !== null) {
        const zoneCheck = validateCaprichoDeliveryZone(formData.delivery_lat, formData.delivery_lng);
        if (!zoneCheck.valid) {
          newErrors.delivery_address = `Lo sentimos, solo hacemos delivery en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Tu ubicación está a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`;
        }
      }
    }
    if (formData.delivery_type === 'dine_in' && !isTableOrder) {
      if (!formData.table_id.trim()) newErrors.table_id = 'Indica tu número de mesa';
    }
    if (isPreorder) {
      if (!formData.preorder_date) (newErrors as any).preorder_date = 'Selecciona una fecha de entrega';
      if (!formData.preorder_time_slot) (newErrors as any).preorder_time_slot = 'Selecciona un horario de entrega';
    }
    setErrors(newErrors);
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${firstErrorKey}"]`);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 50);
    }
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      // Capricho de Canela: validate delivery zone immediately
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho) {
        const zoneCheck = validateCaprichoDeliveryZone(latitude, longitude);
        if (!zoneCheck.valid) {
          setGeoStatus('error');
          setGeoError(`Tu ubicación está fuera de nuestra zona de delivery. Solo entregamos en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Estás a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`);
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-710">
      {/* QR Table Banner */}
      {isTableOrder && tableOrder && <section className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200 flex items-center gap-4" data-id="element-711">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0" data-id="element-712">
            <Utensils className="w-6 h-6 text-emerald-600" data-id="element-713" />
          </div>
          <div data-id="element-714">
            <p className="font-bold text-emerald-900 text-lg" data-id="element-715">
              {tableOrder.tableName} · Autoatención
            </p>
            <p className="text-sm text-emerald-700" data-id="element-716">
              Tu pedido se enviará directamente a esta mesa
            </p>
          </div>
        </section>}

      {/* Contact Info */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-717">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-718">
          <Store className="w-6 h-6 text-brand-primary" data-id="element-719" />
          {t('checkout_title')}
        </h2>

        <div className="space-y-4" data-id="element-720">
          <div data-field="customer_name" data-id="element-721">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-722">
              {t('checkout_name')}
              {errors.customer_name && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-723" />}
            </label>
            <Input value={formData.customer_name} onChange={e => handleChange('customer_name', e.target.value)} error={errors.customer_name} required data-id="element-724" />
          </div>

          <div data-field="customer_phone" data-id="element-725">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-726">
              {t('checkout_phone')}
              {errors.customer_phone && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-727" />}
            </label>
            <div className="relative" data-id="element-728">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-729">
                📞
              </span>
              <Input type="tel" placeholder="88888888" value={formData.customer_phone} onChange={e => setFormData({
              ...formData,
              customer_phone: e.target.value
            })} error={errors.customer_phone} required data-id="element-730" />
            </div>
          </div>

          <div data-field="customer_email" data-id="element-731">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-732">
              {t('checkout_email')}
              {errors.customer_email && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-733" />}
            </label>
            <Input value={formData.customer_email} onChange={e => handleChange('customer_email', e.target.value)} error={errors.customer_email} placeholder="email@ejemplo.com" data-id="element-734" />
          </div>

          <LoyaltyCard phone={formData.customer_phone} restaurantId={restaurant?.id} data-id="element-735" />
        </div>
      </div>

      {/* Preorder Schedule — for preorder restaurants */}
      {isPreorder && preorderWindow && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-736">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-737">
            📦 Entrega Programada
          </h3>

          {/* Schedule Info */}
          <div className={cn('rounded-xl p-4 border mb-5', preorderWindow.isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')} data-id="element-738">
            <p className={cn('font-bold text-sm mb-1', preorderWindow.isOpen ? 'text-emerald-900' : 'text-red-900')} data-id="element-739">
              {preorderWindow.title}
            </p>
            <p className={cn('text-xs', preorderWindow.isOpen ? 'text-emerald-700' : 'text-red-700')} data-id="element-740">
              {preorderWindow.message}
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500" data-id="element-741">
              <span data-id="element-742">📅 Pedidos: Lun–Vie</span>
              <span data-id="element-743">·</span>
              <span data-id="element-744">🚚 Entregas: Sábado 8am–4pm</span>
            </div>
          </div>

          {/* Date Selection */}
          <div className="mb-5" data-id="element-745">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-746">
              Fecha de entrega (sábado)
            </label>
            <div className="space-y-2" data-id="element-747">
              {preorderSaturdays.map(sat => {
            const dateStr = toDateString(sat);
            const isSelected = formData.preorder_date === dateStr;
            return <button key={dateStr} type="button" onClick={() => handleChange('preorder_date' as any, dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-748">
                    📅 {formatDeliveryDate(sat)}
                  </button>;
          })}
            </div>
            {(errors as any).preorder_date && <p className="text-sm text-red-500 mt-1" data-id="element-749">
                {(errors as any).preorder_date}
              </p>}
          </div>

          {/* Time Slot Selection */}
          {formData.preorder_date && <div data-id="element-750">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-751">
                Horario de entrega
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-752">
                {DELIVERY_TIME_SLOTS.map(slot => {
            const isSelected = formData.preorder_time_slot === slot.id;
            return <button key={slot.id} type="button" onClick={() => handleChange('preorder_time_slot' as any, slot.id)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-753">
                      🕐 {slot.label}
                    </button>;
          })}
              </div>
              {(errors as any).preorder_time_slot && <p className="text-sm text-red-500 mt-1" data-id="element-754">
                  {(errors as any).preorder_time_slot}
                </p>}
            </div>}
        </section>}

      {/* Delivery Type */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-755">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-756">
          <MapPin className="w-5 h-5 text-brand-primary" data-id="element-757" />
          {t('checkout_delivery_type')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-758">
          {settings?.accept_delivery && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'delivery'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'delivery' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-759">
              {formData.delivery_type === 'delivery' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-760" />}
              <Navigation className="w-6 h-6 mb-2" data-id="element-761" />
              <span className="font-semibold" data-id="element-762">{t('delivery')}</span>
            </button>}

          {settings?.accept_pickup && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'pickup'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'pickup' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-763">
              {formData.delivery_type === 'pickup' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-764" />}
              <Store className="w-6 h-6 mb-2" data-id="element-765" />
              <span className="font-semibold" data-id="element-766">{t('pickup')}</span>
            </button>}

          {settings?.accept_dine_in && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'dine_in'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-767">
              {formData.delivery_type === 'dine_in' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-768" />}
              <Utensils className="w-6 h-6 mb-2" data-id="element-769" />
              <span className="font-semibold" data-id="element-770">{t('dine_in')}</span>
            </button>}
        </div>

        {/* Conditional Fields based on Delivery Type */}
        <div className="mt-6 space-y-4" data-id="element-771">
          {formData.delivery_type === 'delivery' && <div className="mt-6 space-y-4" data-id="element-772">
              <div data-field="delivery_address" data-id="element-773">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-774">
                  {t('checkout_address')}
                  {errors.delivery_address && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-775" />}
                </label>
                <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-776" />
              </div>

              <div className="space-y-2" data-field="maps_url" data-id="element-777">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5" data-id="element-778">
                  Ubicación GPS (Obligatorio)
                  {(errors as any).maps_url && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-779" />}
                </label>

                <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && !(errors as any).maps_url && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'idle' && (errors as any).maps_url && 'border-red-300 text-red-600 bg-red-50 animate-pulse', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-780">
                  {geoStatus === 'idle' && <>
                      <Navigation className="w-5 h-5 mr-2" data-id="element-781" />
                      Usar mi ubicación actual
                    </>}
                  {geoStatus === 'loading' && <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-782" />
                      Obteniendo ubicación...
                    </>}
                  {geoStatus === 'success' && <>
                      <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-783" />
                      Ubicación capturada
                    </>}
                  {geoStatus === 'error' && <>
                      <AlertCircle className="w-5 h-5 mr-2" data-id="element-784" />
                      Reintentar ubicación
                    </>}
                </button>

                {(errors as any).maps_url && geoStatus === 'idle' && <p className="text-sm text-red-500 flex items-center gap-1.5 px-1" data-id="element-785">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-786" />
                    {(errors as any).maps_url}
                  </p>}

                {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-787">
                    <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-788">
                      📍 Ver en el mapa
                    </a>
                    <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-789">
                      Actualizar
                    </button>
                  </div>}

                {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-790">{geoError}</p>}
              </div>
            </div>}

          {formData.delivery_type === 'dine_in' && <Input label="Número o nombre de mesa" placeholder="Ej. Mesa 4, Barra, etc." value={formData.table_id} onChange={e => handleChange('table_id', e.target.value)} error={errors.table_id} required data-id="element-791" />}
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-792">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-793">
          <CreditCard className="w-5 h-5 text-brand-primary" data-id="element-794" />
          {t('checkout_payment')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-795">
          {restaurant?.accepts_cash && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          payment_method: 'cash'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'cash' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-796">
              {formData.payment_method === 'cash' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-797" />}
              <Banknote className="w-6 h-6 mb-2" data-id="element-798" />
              <span className="font-semibold" data-id="element-799">{t('checkout_cash')}</span>
            </button>}

          {restaurant?.accepts_card && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          payment_method: 'card'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'card' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-800">
              {formData.payment_method === 'card' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-801" />}
              <CreditCard className="w-6 h-6 mb-2" data-id="element-802" />
              <span className="font-semibold" data-id="element-803">{t('checkout_card')}</span>
            </button>}

          {restaurant?.accepts_sinpe && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          payment_method: 'sinpe'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'sinpe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-804">
              {formData.payment_method === 'sinpe' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-805" />}
              <Smartphone className="w-6 h-6 mb-2" data-id="element-806" />
              <span className="font-semibold" data-id="element-807">{t('checkout_sinpe')}</span>
            </button>}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-808">
        <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-809">
          {t('checkout_notes')}
        </label>
        <Textarea value={formData.notes} onChange={e => setFormData(prev => ({
        ...prev,
        notes: e.target.value
      }))} placeholder={t('checkout_notes_placeholder')} className="resize-none h-24" data-id="element-810" />
      </div>

      {/* Terms and Conditions — hidden for returning customers */}
      {!isReturningCustomer && <div className="flex items-start gap-3 px-2" data-id="element-811">
          <div className="flex items-center h-5" data-id="element-812">
            <input id="terms" type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-brand-primary focus:ring-brand-primary" data-id="element-813" />
          </div>
          <div className="text-sm" data-id="element-814">
            <label htmlFor="terms" className="font-medium text-gray-900" data-id="element-815">
              {t('checkout_terms')}
            </label>
            <p className="text-gray-600" data-id="element-816">{t('checkout_terms')}</p>
          </div>
        </div>}

      <Button type="submit" className="w-full h-14 text-lg font-bold" disabled={isLoading || !termsAccepted} data-id="element-817">
        {isLoading ? <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-818" />
            {t('checkout_placing')}
          </> : t('checkout_place_order')}
      </Button>

      {/* Terms Modal */}
      {showTermsModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} data-id="element-819">
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-id="element-820">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-821">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-822">
                Términos y Condiciones
              </h2>
              <button onClick={() => setShowTermsModal(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-823">
                <AlertCircle className="w-5 h-5" data-id="element-824" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-825">
              <h3 className="font-bold text-gray-900" data-id="element-826">
                1. Aceptación de los Términos
              </h3>
              <p data-id="element-827">
                Al realizar un pedido a través de NOIZA, usted acepta estos
                términos y condiciones en su totalidad. Si no está de acuerdo
                con alguna parte, no debe utilizar nuestro servicio.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-828">
                2. Servicio de Pedidos
              </h3>
              <p data-id="element-829">
                NOIZA actúa como intermediario tecnológico entre el cliente y el
                restaurante. Los pedidos se procesan y confirman directamente
                con el restaurante a través de WhatsApp.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-830">3. Precios y Pagos</h3>
              <p data-id="element-831">
                Los precios mostrados incluyen impuestos. El pago se realiza
                directamente al restaurante según el método seleccionado
                (efectivo, SINPE Móvil o tarjeta). NOIZA no procesa pagos.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-832">4. Entregas</h3>
              <p data-id="element-833">
                Los tiempos de entrega son estimados y pueden variar según la
                demanda. El costo de envío se muestra antes de confirmar el
                pedido. El restaurante es responsable de la entrega.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-834">5. Cancelaciones</h3>
              <p data-id="element-835">
                Las cancelaciones deben coordinarse directamente con el
                restaurante a través de WhatsApp. Una vez que el pedido está en
                preparación, no se garantiza la cancelación.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-836">6. Datos Personales</h3>
              <p data-id="element-837">
                Recopilamos nombre, teléfono y ubicación únicamente para
                procesar su pedido. No compartimos sus datos con terceros más
                allá del restaurante seleccionado. Su información se maneja
                conforme a la Ley de Protección de Datos de Costa Rica.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-838">7. Responsabilidad</h3>
              <p data-id="element-839">
                NOIZA no es responsable por la calidad de los alimentos, tiempos
                de entrega o cualquier inconveniente con el restaurante.
                Cualquier reclamo debe dirigirse directamente al
                establecimiento.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-840">8. Modificaciones</h3>
              <p data-id="element-841">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Los cambios serán efectivos al publicarse en
                la plataforma.
              </p>
              <p className="text-xs text-gray-400 pt-4 border-t border-gray-100" data-id="element-842">
                Última actualización: Marzo 2026 · Los Santos, Costa Rica
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0" data-id="element-843">
              <button onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
          }} className="w-full py-3 rounded-xl bg-brand-amber text-white font-bold text-sm hover:bg-amber-600 transition-colors" data-id="element-844">
                Aceptar Términos y Condiciones
              </button>
            </div>
          </div>
        </div>}
    </form>;
}